import React, { useState } from 'react';
import { Routes } from 'routes';
import { fetchAllRegionResortsTopSnow } from 'api/region/static';
import {
  fetchBackgroundImages,
  fetchLastNewsWeather,
  fetchRegionsListGuide,
  fetchSnowWeatherHomeFeatured,
} from 'api/home/static';
import { IBackgroundImages, INews } from 'api/home/types';
import { IRegionAllPathsInfo, IRegionFeaturedResort, IRegionResortsNonPaginated } from 'api/region/types';
import { AdUnitContext } from 'googlead/components/AdUnitContext';
import { TopAd } from 'googlead/components/TopAd';
import { RegularAd } from 'googlead/components/RegularAd';
import { RouteAndAnalyticsProps } from 'page-components/pathTypes';
import IntegrityError from 'page-components/400';
import { HeadMeta } from 'components/common/HeadMeta';
import { HeaderNav, HeaderNavCurLink } from 'components/common/HeaderNav';
import { Layout } from 'components/resort/Layout/Layout';
import { Main } from 'components/resort/Layout/Main';
import { BottomMountains } from 'components/blocks/BottomMountains';
import { Footer } from 'components/blocks/Footer';
import { BoxWithShadow } from 'components/blocks/BoxWithShadow';
import { BlockHeader } from 'components/blocks/BlockHeader';
import { BlockContent } from 'components/blocks/BlockContent';
import { ViewRegions } from 'components/home/ViewRegions';
import { NewsList } from 'components/home/NewsList';
import Grid from 'components/common/Grid/Grid';
import { createRegionWeatherGridColumns } from 'components/region/WeatherReports/RegionWeatherGridColumns';
import { getPortalOptions } from 'util/getPortalConfig';
import { useQueryWithInitialValue } from 'util/useQueryWithInitialValue';
import { useOTSTranslation } from 'i18n/useOTSTranslation';

import weatherGridStyles from 'components/region/WeatherReports/styles.module.scss';
import { HeaderClickEvent, IGridColumn, IGridSorting } from 'components/common/Grid/types';
import { wrapServerSideProps, wrapStaticProps } from 'util/wrapProps';
import { HeroNav } from 'components/common/HeroNav';

import { AiAssistantWidget } from 'components/common/AiAssistantWidget';

export type SnowWeatherHomePageProps = {
  regions: IRegionAllPathsInfo,
  featured: IRegionFeaturedResort,
  lastNewsWeather: Array<INews> | null,
  backgroundImage: IBackgroundImages,
  topSnowResorts: IRegionResortsNonPaginated,
} & RouteAndAnalyticsProps;

export const TEMPLATE_ID = '2.0';
export const TEMPLATE_NAME = 'Snow & Weather Home';

export default function SnowWeatherHomePage({
  regions, featured, lastNewsWeather, backgroundImage,
  topSnowResorts: preTopSnowResorts,
}: SnowWeatherHomePageProps) {
  const { t } = useOTSTranslation('common');
  const { t: seoT } = useOTSTranslation('seo');
  const { hasNews } = getPortalOptions();
  const [sorting, setSorting] = useState<IGridSorting>({});
  const columns = createRegionWeatherGridColumns('top-72');

  const {
    current: topSnowResorts, isFetching,
  } = useQueryWithInitialValue(['home-fetchAllRegionResortsTopSnow', sorting],
    async () => fetchAllRegionResortsTopSnow(sorting),
    preTopSnowResorts);
  const resorts = topSnowResorts.data;

  const onSortingChange = (event: HeaderClickEvent, column: IGridColumn, sortingValues: IGridSorting) => {
    setSorting(sortingValues);
  };

  if (!featured || !backgroundImage) {
    return <IntegrityError />;
  }

  return (
    <AdUnitContext
      adType="home"
      adTKey="other home"
      meta={{
        Fs: undefined,
        Rs: undefined,
        Th: undefined,
        Tl: undefined,
        snow: undefined,
        Wc: undefined,
      }}
    >
      <HeadMeta
        title={seoT('Other.skireport.title')}
        description={seoT('Other.skireport.description')}
        image={backgroundImage.image}
      />
      <HeaderNav current={HeaderNavCurLink.SNOW_REPORTS} />
      <TopAd id="skireport" />
      <div className="container-xl content-container">
        <HeroNav
          title={t('snowWeather.home.heroTitle')}
          backgroundPhoto={backgroundImage.image}
        />

        <Layout className="layout-container">
          <Main>
            <BoxWithShadow>
              <BlockHeader title={t('snowWeather.home.seoTitle')} />
              <BlockContent>
                {t('snowWeather.home.seoText')}
              </BlockContent>
            </BoxWithShadow>
            <Grid
              data={resorts}
              total={columns.length}
              columns={columns}
              noValueSymbol={t('common.dash')}
              className={weatherGridStyles.collabsibleTable}
              isLoading={isFetching}
              onSortingChange={onSortingChange}
            />
            <RegularAd isFirst />
            <AiAssistantWidget
              templateId={TEMPLATE_ID}
              margin={{ marginTop: '-0.75rem', marginBottom: '1.75rem' }}
            />
            {regions && <ViewRegions regions={regions} link={Routes.regionSnowReport} />}
            {hasNews && lastNewsWeather && (
            <NewsList
              title={t('homePage.latestNews')}
              news={lastNewsWeather}
              weatherNews
              areNewsFresh
            />
            )}
          </Main>
        </Layout>
      </div>
      <BottomMountains />
      <Footer />
    </AdUnitContext>
  );
}

export const fetchProps = async (): Promise<SnowWeatherHomePageProps> => {
  const regions = await fetchRegionsListGuide();
  const featured = await fetchSnowWeatherHomeFeatured();
  const { hasNews } = getPortalOptions();
  const lastNewsWeather = hasNews ? (await fetchLastNewsWeather()) : null;
  const backgroundImage = await fetchBackgroundImages();

  return {
    type: 'global',
    route: 'skireport',
    topSnowResorts: await fetchAllRegionResortsTopSnow(),
    regions,
    featured,
    lastNewsWeather,
    backgroundImage,
    templateName: TEMPLATE_NAME,
    templateId: TEMPLATE_ID,
    prefetch: false,
  };
};

// This function gets called at build time
export const getSnowWeatherHomePageStaticProps = wrapStaticProps(fetchProps);

export const getSnowWeatherHomePageServerSideProps = wrapServerSideProps(fetchProps);
